import { Article, RegisteredModels } from "@tm/models"
import { Container } from "@tm/nexus"
import { useQuery } from "react-query"
import { useMemo } from "react"
import { useAdvertisement } from "@tm/data/erpConnect"
import { getArticlesFromResponse, mapAdvertisementToSupplierArticle, TraderArticle } from "./helpers"

export function useAdvertisementParts(advertisementId: string | undefined, pageIndex: number, pageSize: number) {
    const { advertisementItems, advertisementItemsLoading, advertisementItemsLoaded } = useAdvertisement(advertisementId)

    const startIndex = pageIndex * pageSize
    const endIndex = startIndex + pageSize
    const itemsOfCurrentPage = advertisementItems.slice(startIndex, endIndex)
    const totalPages = Math.ceil(advertisementItems.length / pageSize)

    const wholesalerArticleNumbers = itemsOfCurrentPage.map((x) => x.wholesalerArticleNumber).filter((x) => x)

    const { data: wholesalerParts = [], isLoading: wholesalerPartsLoading } = useQuery(
        ["offers_advertisement-wholesaler", wholesalerArticleNumbers],
        () =>
            Container.getInstance<Record<string, Array<Article>>>(RegisteredModels.Articles_ByWholesalerArticleNumbers)
                .subscribe(wholesalerArticleNumbers)
                .load(),
        {
            enabled: !!wholesalerArticleNumbers.length,
            staleTime: 1000 * 60 * 60 * 24, // keep data for 1 day
            select: getArticlesFromResponse,
        }
    )

    const supplierArticles = mapAdvertisementToSupplierArticle(itemsOfCurrentPage)

    const { data: supplierParts = [], isLoading: supplierPartsLoading } = useQuery(
        ["offers_advertisement-supplier-parts", supplierArticles],
        () =>
            Container.getInstance<Record<string, Array<Article>>>(RegisteredModels.Articles_BySupplierArticleNumbers)
                .subscribe(supplierArticles)
                .load(),
        {
            enabled: !!supplierArticles.length,
            staleTime: 1000 * 60 * 60 * 24, // keep data for 1 day
            select: getArticlesFromResponse,
        }
    )

    const normalize = (val?: string): string =>
        (val ?? "")
            .replace(/[\s\-_.]+/g, "")
            .trim()
            .toLowerCase()

    const parts = useMemo(() => {
        const sortedParts = [...wholesalerParts, ...supplierParts]

        sortedParts.sort((a, b) => {
            const getKey = (part: Article | TraderArticle): string | undefined => {
                if ("isTraderArticle" in part) {
                    return normalize(part.traderArticleNumber)
                }
                return normalize(part.supplierArticleNo)
            }

            const aKey = getKey(a)
            const bKey = getKey(b)

            const aIndex = advertisementItems.findIndex(
                (ad) => normalize(ad.wholesalerArticleNumber) === aKey || normalize(ad.dataSupplierArticleNumber) === aKey
            )

            const bIndex = advertisementItems.findIndex(
                (ad) => normalize(ad.wholesalerArticleNumber) === bKey || normalize(ad.dataSupplierArticleNumber) === bKey
            )

            return aIndex - bIndex
        })

        return sortedParts
    }, [wholesalerParts, supplierParts, advertisementItems])

    return {
        advertisementItems,
        parts,
        advertisementItemsLoaded,
        isLoading: advertisementItemsLoading || wholesalerPartsLoading || supplierPartsLoading,
        totalPages,
    }
}
